import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {Object} AxProductMediaGalleryRefs
 * @property {HTMLElement} [desktopRoot] - Desktop layout container
 * @property {HTMLElement} [mainImages] - Desktop main image wrapper
 * @property {HTMLElement[]} [mainItems] - Desktop main image items (one per media, only one visible)
 * @property {HTMLElement} [thumbsList] - Desktop thumbnails grid container
 * @property {HTMLButtonElement[]} [thumbs] - Desktop thumbnail buttons (all, including hidden). The 6th may carry class "ax-gallery__thumb--overflow".
 * @property {HTMLElement} [mobileRoot] - Mobile swiper wrapper
 * @property {HTMLElement & { swiper?: any; initialize?: () => void }} [swiper] - swiper-container element
 * @property {HTMLElement} [swiperFallback] - SSR fallback holding slides before Swiper init
 * @property {HTMLElement[]} [compositeOverlays] - Desktop + mobile composite preview overlays (sibling to mainItems / swiper)
 * @property {HTMLImageElement[]} [compositeImgs] - <img> inside each composite overlay
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialog] - Optional zoom dialog
 */

const BAND_PREVIEW_EVENT = 'product-media:band-preview';

/**
 * Withings product media gallery.
 *
 * Desktop: single large square image + thumbnail grid (max 6 visible, 6th thumbnail
 * becomes a "+X" overflow trigger that reveals remaining thumbs in a 2nd row).
 * Click large image -> zoom-dialog (if enabled).
 *
 * Mobile: Swiper carousel with dots pagination. Lazy loading via CFH observer —
 * only the first slide is eager-loaded, adjacent slides preload on slideChange.
 *
 * @extends {Component<AxProductMediaGalleryRefs>}
 */
class AxProductMediaGallery extends Component {
  /** @type {number} */
  #activeIndex = 0;

  /** @type {number} */
  #mobileActiveIndex = 0;

  /** @type {number} */
  #thumbLimit = 6;

  /** @type {boolean} */
  #thumbsExpanded = false;

  /** @type {(() => void) | null} */
  #swiperCleanup = null;

  /** @type {IntersectionObserver | null} */
  #revealObserver = null;

  /** @type {string | null} Currently previewed composite URL (null when cleared). */
  #compositeUrl = null;

  /** @type {number} rAF handle throttling the thumb scroll-state updates. */
  #thumbsScrollRaf = 0;

  /** @type {HTMLElement | null} Element the thumbs scroll listener is bound to. */
  #thumbsScrollEl = null;

  /** @type {{ active: boolean, moved: boolean, captured: boolean, startX: number, startScroll: number, pointerId: number }} */
  #thumbDrag = { active: false, moved: false, captured: false, startX: 0, startScroll: 0, pointerId: -1 };

  async connectedCallback() {
    super.connectedCallback();

    this.#syncDesktop(0);
    this.#observeThumbsReveal();
    this.#attachThumbsScroll();
    document.addEventListener('model:update', this.#onModelUpdate);
    document.addEventListener('variant:update', this.#onVariantUpdate);
    document.addEventListener(BAND_PREVIEW_EVENT, this.#onBandPreview);

    if (this.refs.swiper && this.refs.swiperFallback) {
      await customElements.whenDefined('swiper-container');
      this.#initSwiperFrom(this.refs.swiper, this.refs.swiperFallback);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('model:update', this.#onModelUpdate);
    document.removeEventListener('variant:update', this.#onVariantUpdate);
    document.removeEventListener(BAND_PREVIEW_EVENT, this.#onBandPreview);
    this.#teardownSwiper();

    this.#revealObserver?.disconnect();
    this.#revealObserver = null;

    if (this.#thumbsScrollRaf) cancelAnimationFrame(this.#thumbsScrollRaf);
    this.#thumbsScrollRaf = 0;

    this.#thumbsScrollEl?.removeEventListener('scroll', this.#onThumbsScroll);
    this.#thumbsScrollEl?.removeEventListener('pointerdown', this.#onThumbsPointerDown);
    this.#thumbsScrollEl?.removeEventListener('pointermove', this.#onThumbsPointerMove);
    this.#thumbsScrollEl?.removeEventListener('pointerup', this.#onThumbsPointerUp);
    this.#thumbsScrollEl?.removeEventListener('pointercancel', this.#onThumbsPointerUp);
    this.#thumbsScrollEl = null;
  }

  /** Rebuild gallery state after model-picker morphs <main>. */
  #onModelUpdate = async () => {
    this.clearComposite();
    this.#teardownSwiper();
    this.#resetDeferredMedia();
    this.#activeIndex = 0;
    this.#mobileActiveIndex = 0;
    this.#thumbsExpanded = false;
    this.#revealObserver?.disconnect();
    this.#revealObserver = null;
    // Wait a frame so refs point at the fresh DOM before rebuilding.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    this.#observeThumbsReveal();
    this.#attachThumbsScroll();
    this.#syncDesktop(0);
    await this.#setupMobileSwiper();
  };

  /** Strip injected/inline media + data-media-loaded so a rejoined slide re-clones the fresh template. */
  #resetDeferredMedia() {
    for (const el of this.querySelectorAll('deferred-media, product-model')) {
      /** @type {any} */ (el).pauseMedia?.();
      for (const node of el.querySelectorAll(':scope > video, :scope > iframe, :scope > model-viewer')) {
        node.remove();
      }
      el.removeAttribute('data-media-loaded');
      /** @type {any} */ (el).isPlaying = false;
    }
  }

  /**
   * Variant change on the SAME product (e.g. wristband size 18mm → 20mm).
   * The variant-picker only morphs itself in this case, so the gallery has to
   * jump to the variant's featured media on its own. Every media is already in
   * the DOM (requires the block's "hide_variants" setting to be off), so we just
   * select the matching item — no re-fetch, same path as a thumbnail click.
   *
   * Combined-listing changes load a different product and are handled by the
   * full `<main>` morph + `model:update`, so we skip them here.
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    const detail = /** @type {{ resource?: { featured_media?: { id?: string | number } }, data?: { productId?: string, newProduct?: unknown } } | undefined} */ (
      /** @type {CustomEvent} */ (event).detail
    );

    if (detail?.data?.newProduct) return;

    // PDP can host several product forms — only react to this gallery's product.
    const productId = this.dataset.productId;
    if (productId && detail?.data?.productId && detail.data.productId !== productId) return;

    const mediaId = detail?.resource?.featured_media?.id;
    if (mediaId == null) return; // variant has no assigned media → keep current

    const index = this.#indexOfMediaId(String(mediaId));
    if (index < 0) return; // media not in gallery (hide_variants on, or card-reserved media[0])

    this.#syncDesktop(index);

    const swiper = /** @type {{ swiper?: { slideTo?: (i: number) => void, slides?: HTMLElement[] } } | null} */ (
      this.querySelector('swiper-container.ax-gallery__swiper')
    )?.swiper;
    if (swiper) {
      // Load the target slide's lazy image up-front. Relying on
      // `slideChangeTransitionStart` to trigger the preload leaves the slide
      // blank (image still on lazy-src) while it slides into view on mobile.
      const targetSlide = swiper.slides?.[index];
      if (targetSlide) this.#removeLazyKeep(targetSlide);
      swiper.slideTo?.(index);
    }
  };

  /**
   * Find the gallery item index matching a Shopify media id.
   * @param {string} mediaId
   * @returns {number} Index in mainItems, or -1 if not found.
   */
  #indexOfMediaId(mediaId) {
    const items = this.refs.mainItems ?? [];
    for (const [i, item] of items.entries()) {
      if (item.querySelector('[data-media-id]')?.getAttribute('data-media-id') === mediaId) return i;
    }
    return -1;
  }

  /** @param {Event} event */
  #onBandPreview = (event) => {
    const detail = /** @type {{ url?: string | null } | undefined} */ (
      /** @type {CustomEvent} */ (event).detail
    );
    const url = detail?.url;
    if (url) this.setComposite(url);
    else this.clearComposite();
  };

  /**
   * Public: show a composite "watch + band" image as overlay on top of the
   * active main media. Toggled by `<band-upsell-drawer>` / `<band-upsell-block>`
   * via the `product-media:band-preview` event.
   *
   * The overlay is held hidden until the image actually loads — avoids a flash
   * of the empty 0.03 backdrop when the file 404s. On 404 / decode error
   * `clearComposite()` is called silently (revert to original gallery media).
   *
   * @param {string} url
   */
  setComposite(url) {
    const overlays = this.refs.compositeOverlays ?? [];
    const imgs = this.refs.compositeImgs ?? [];
    if (!overlays.length || !imgs.length) return;

    this.#compositeUrl = url;

    for (const img of imgs) {
      img.onload = () => this.#syncCompositeVisibility();
      img.onerror = () => this.clearComposite();
      img.src = url;
    }

    // Do NOT call #syncCompositeVisibility here — wait for onload. Otherwise
    // we briefly show an empty overlay before the 404 / decode error fires.
  }

  /** Public: hide the composite overlay and revert to the gallery's own media. */
  clearComposite() {
    const overlays = this.refs.compositeOverlays ?? [];
    const imgs = this.refs.compositeImgs ?? [];

    this.#compositeUrl = null;

    for (const overlay of overlays) overlay.hidden = true;
    for (const img of imgs) {
      img.onerror = null;
      img.removeAttribute('src');
    }
  }

  /**
   * Per-overlay show/hide logic: only show the desktop overlay when the
   * desktop activeIndex is 0, and only the mobile overlay when the swiper
   * realIndex is 0. If no composite URL is currently set, all overlays are
   * hidden regardless.
   */
  #syncCompositeVisibility() {
    const overlays = this.refs.compositeOverlays ?? [];
    if (!overlays.length) return;

    for (const overlay of overlays) {
      if (!this.#compositeUrl) {
        overlay.hidden = true;
        continue;
      }
      const isMobile = overlay.classList.contains('ax-gallery__composite--mobile');
      const activeIndex = isMobile ? this.#mobileActiveIndex : this.#activeIndex;
      overlay.hidden = activeIndex !== 0;
    }
  }

  async #setupMobileSwiper() {
    await customElements.whenDefined('swiper-container');
    const swiperEl = /** @type {HTMLElement & { swiper?: any; initialize?: () => void } | null} */ (
      this.querySelector('swiper-container.ax-gallery__swiper')
    );
    const swiperFallback = /** @type {HTMLElement | null} */ (this.querySelector('[data-ax-fallback]'));
    if (!swiperEl || !swiperFallback) return;
    if (swiperEl.querySelector('swiper-slide')) return; // already initialized
    this.#initSwiperFrom(swiperEl, swiperFallback);
  }

  #teardownSwiper() {
    this.#swiperCleanup?.();
    this.#swiperCleanup = null;

    const swiperEl = /** @type {HTMLElement & { swiper?: any } | null} */ (
      this.querySelector('swiper-container.ax-gallery__swiper')
    );
    try {
      swiperEl?.swiper?.destroy(true, true);
    } catch (_) {
      // noop
    }
  }

  /**
   * Unpause the thumb reveal animation the first time the grid enters the viewport.
   * CSS keeps the animation paused by default so there is no flash if JS is slow.
   */
  #observeThumbsReveal() {
    const { thumbsList } = this.refs;
    if (!thumbsList) return;

    if (typeof IntersectionObserver === 'undefined') {
      thumbsList.classList.add('is-revealed');
      return;
    }

    this.#revealObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            thumbsList.classList.add('is-revealed');
            this.#revealObserver?.disconnect();
            this.#revealObserver = null;
            return;
          }
        }
      },
      { threshold: 0.1 }
    );

    this.#revealObserver.observe(thumbsList);
  }

  /**
   * Public: called from the zoom trigger (on:click="/zoom/index").
   * @param {number} index
   * @param {PointerEvent} event
   */
  zoom(index, event) {
    this.refs.zoomDialog?.open(index, event);
  }

  /**
   * Public: preload a specific main image (on:pointerenter on thumb).
   * Dispatches removeLazyKeep so the CFH observer swaps lazy-src -> src.
   * @param {number} index
   */
  preloadImage(index) {
    const item = this.refs.mainItems?.[index];
    if (!item) return;
    this.#removeLazyKeep(item);
  }

  /**
   * Public: thumbnail click (on:click="/handleThumbClick/index").
   * @param {number} index
   */
  handleThumbClick(index) {
    const thumb = this.refs.thumbs?.[index];
    const isOverflowTrigger =
      !this.#thumbsExpanded && thumb?.classList.contains('ax-gallery__thumb--overflow');

    if (isOverflowTrigger) {
      this.#expandThumbs();
      return;
    }

    this.#syncDesktop(index);
  }

  /**
   * Public: scroll the expanded thumbnail row (on:click="/scrollThumbs/-1|1").
   * No-op while collapsed — the row only scrolls once expanded to a single line.
   * @param {number|string} direction - -1 (towards start) or 1 (towards end).
   */
  scrollThumbs(direction) {
    const list = this.refs.thumbsList;
    if (!list) return;

    const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
    list.scrollBy({ left: Number(direction) * list.clientWidth * 0.8, behavior });
  }

  /**
   * Bind (or rebind after a morph) the scroll listener that keeps the edge fade +
   * arrows in sync, plus mouse drag-to-scroll on the expanded strip. Scroll only fires
   * once expanded, so binding while collapsed is free. The framework does not delegate
   * `scroll` (non-bubbling), so these are attached manually.
   */
  #attachThumbsScroll() {
    const next = this.refs.thumbsList ?? null;
    if (this.#thumbsScrollEl === next) return;

    this.#thumbsScrollEl?.removeEventListener('scroll', this.#onThumbsScroll);
    this.#thumbsScrollEl?.removeEventListener('pointerdown', this.#onThumbsPointerDown);
    this.#thumbsScrollEl = next;
    this.#thumbsScrollEl?.addEventListener('scroll', this.#onThumbsScroll, { passive: true });
    this.#thumbsScrollEl?.addEventListener('pointerdown', this.#onThumbsPointerDown);
  }

  /**
   * Mouse drag-to-scroll on the expanded thumbnail strip. Mouse / pen only — touch keeps
   * native scrolling. Capture is deferred until the drag threshold so a plain click still
   * reaches the thumbnail button.
   * @param {PointerEvent} event
   */
  #onThumbsPointerDown = (event) => {
    if (event.pointerType === 'touch') return;

    const list = this.#thumbsScrollEl;
    if (!list || list.scrollWidth <= list.clientWidth) return;

    this.#thumbDrag = {
      active: true,
      moved: false,
      captured: false,
      startX: event.clientX,
      startScroll: list.scrollLeft,
      pointerId: event.pointerId,
    };
    list.addEventListener('pointermove', this.#onThumbsPointerMove);
    list.addEventListener('pointerup', this.#onThumbsPointerUp);
    list.addEventListener('pointercancel', this.#onThumbsPointerUp);
  };

  /** @param {PointerEvent} event */
  #onThumbsPointerMove = (event) => {
    if (!this.#thumbDrag.active) return;

    const list = this.#thumbsScrollEl;
    if (!list) return;

    const dx = event.clientX - this.#thumbDrag.startX;
    if (!this.#thumbDrag.moved) {
      if (Math.abs(dx) <= 4) return;
      this.#thumbDrag.moved = true;
      list.classList.add('is-dragging', 'is-grabbing');
      try {
        list.setPointerCapture(event.pointerId);
        this.#thumbDrag.captured = true;
      } catch (_) {
        // drag still works without capture
      }
    }
    list.scrollLeft = this.#thumbDrag.startScroll - dx;
  };

  /** @param {PointerEvent} event */
  #onThumbsPointerUp = (event) => {
    const list = this.#thumbsScrollEl;
    if (!list) return;

    list.removeEventListener('pointermove', this.#onThumbsPointerMove);
    list.removeEventListener('pointerup', this.#onThumbsPointerUp);
    list.removeEventListener('pointercancel', this.#onThumbsPointerUp);
    if (this.#thumbDrag.captured) {
      try {
        list.releasePointerCapture(event.pointerId);
      } catch (_) {
        // already released
      }
    }
    list.classList.remove('is-grabbing');

    const moved = this.#thumbDrag.moved;
    this.#thumbDrag.active = false;

    if (moved) {
      requestAnimationFrame(() => list.classList.remove('is-dragging'));
    } else {
      list.classList.remove('is-dragging');
    }
  };

  /** rAF-throttled scroll handler → refresh the scroll-state classes. */
  #onThumbsScroll = () => {
    if (this.#thumbsScrollRaf) return;

    this.#thumbsScrollRaf = requestAnimationFrame(() => {
      this.#thumbsScrollRaf = 0;
      this.#updateThumbsScrollState();
    });
  };

  /**
   * Toggle is-scrollable-start / is-scrollable-end on the thumbs row depending on
   * whether content is hidden on each side. Drives the CSS edge fade + arrows.
   */
  #updateThumbsScrollState() {
    const list = this.refs.thumbsList;
    if (!list) return;

    // Forgiving edge threshold so snap/border rounding near an edge doesn't flag a fade.
    const edge = 8;
    const maxScroll = list.scrollWidth - list.clientWidth;
    const hasOverflow = maxScroll > edge;

    list.classList.toggle('is-scrollable-start', hasOverflow && list.scrollLeft > edge);
    list.classList.toggle('is-scrollable-end', hasOverflow && list.scrollLeft < maxScroll - edge);
  }

  /**
   * Public: keyboard navigation on the tablist (on:keydown on thumbsList).
   * @param {KeyboardEvent} event
   */
  handleThumbsKeydown(event) {
    const { thumbs } = this.refs;
    if (!thumbs?.length) return;

    const total = thumbs.length;

    /** @type {Record<string, number>} */
    const deltas = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -1,
      ArrowDown: 1,
      Home: -this.#activeIndex,
      End: total - 1 - this.#activeIndex,
    };

    const offset = deltas[event.key];
    if (offset === undefined) return;

    event.preventDefault();
    const nextIndex = (this.#activeIndex + offset + total) % total;

    if (!this.#thumbsExpanded && nextIndex >= this.#thumbLimit) {
      this.#expandThumbs();
    }

    this.#syncDesktop(nextIndex);

    const nextThumb = thumbs[nextIndex];
    nextThumb?.focus();
    nextThumb?.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      inline: 'nearest',
      block: 'nearest',
    });
  }

  /**
   * Sync the desktop main image + thumb aria-selected state.
   * @param {number} index
   */
  #syncDesktop(index) {
    const { mainItems, thumbs } = this.refs;
    if (!mainItems?.length || !thumbs?.length) return;

    const previousActiveItem = mainItems[this.#activeIndex];
    if (previousActiveItem && this.#activeIndex !== index) {
      this.#pauseDeferredMedia(previousActiveItem);
    }

    this.#activeIndex = index;

    for (const [i, item] of mainItems.entries()) {
      const isActive = i === index;
      item.hidden = !isActive;
    }

    for (const [i, thumb] of thumbs.entries()) {
      const isActive = i === index;
      thumb.setAttribute('aria-selected', String(isActive));
      thumb.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    const activeItem = mainItems[index];
    if (activeItem) {
      this.#removeLazyKeep(activeItem);
      this.#maybeAutoplayVideo(activeItem);
    }

    this.#syncCompositeVisibility();
  }

  /**
   * If the active item contains a deferred-media video and autoplay is enabled
   * (data-autoplay-video), reveal + play it.
   * @param {HTMLElement} item
   */
  #maybeAutoplayVideo(item) {
    if (this.dataset.autoplayVideo !== 'true') return;

    // Desktop + mobile layouts both live in the DOM; only autoplay the one that
    // is actually visible. Otherwise the hidden layout's video plays and its
    // `mediaStartedPlaying` event pauses the visible one (frozen-on-frame-0 bug).
    if (item.offsetParent === null) return;

    const isVideo =
      item.classList.contains('product-media-container--video') ||
      item.classList.contains('product-media-container--external_video');
    if (!isVideo) return;

    const deferred = /** @type {any} */ (item.querySelector('deferred-media'));
    if (!deferred) return;

    if (deferred.getAttribute('data-media-loaded')) {
      deferred.playMedia?.();
    } else {
      deferred.showDeferredMedia?.();
    }
  }

  /**
   * Pause any deferred-media inside the given item.
   * @param {HTMLElement} item
   */
  #pauseDeferredMedia(item) {
    const deferred = /** @type {any} */ (item.querySelector('deferred-media'));
    deferred?.pauseMedia?.();
  }

  /**
   * Reveal hidden thumbs (index >= thumbLimit) and demote the overflow trigger.
   * Visibility is driven by CSS via the is-expanded class; JS only releases
   * the lazy-keep on the revealed thumbs so the observer can load them.
   */
  #expandThumbs() {
    const { thumbs, thumbsList } = this.refs;
    if (!thumbs?.length || !thumbsList) return;

    this.#thumbsExpanded = true;
    thumbsList.classList.add('is-expanded');

    const overflowBtn = thumbs[this.#thumbLimit - 1];
    overflowBtn?.classList.remove('ax-gallery__thumb--overflow');
    overflowBtn?.removeAttribute('data-overflow-count');

    for (const [i, thumb] of thumbs.entries()) {
      if (i >= this.#thumbLimit) {
        this.#removeLazyKeep(thumb);
      }
    }

    // Reflect the new single-row overflow state (edge fade + arrows) once laid out.
    requestAnimationFrame(() => this.#updateThumbsScrollState());
  }

  /**
   * Initialize Swiper on mobile viewport.
   * Follows the theme's SSR-fallback pattern: extract slides from [data-ax-fallback],
   * wrap in <swiper-slide>, call .initialize().
   *
   * @param {HTMLElement & { swiper?: any; initialize?: () => void }} swiperEl
   * @param {HTMLElement} swiperFallback
   */
  #initSwiperFrom(swiperEl, swiperFallback) {
    if (typeof swiperEl.initialize !== 'function') return;

    const slideElements = [...swiperFallback.children];
    if (!slideElements.length) return;

    const mobileRoot = this.refs.mobileRoot;
    const reservedHeight = mobileRoot?.getBoundingClientRect().height ?? 0;
    if (mobileRoot && reservedHeight > 0) {
      mobileRoot.style.minHeight = `${reservedHeight}px`;
    }

    for (const slideEl of slideElements) {
      const swiperSlide = document.createElement('swiper-slide');
      swiperSlide.appendChild(slideEl);
      swiperEl.appendChild(swiperSlide);
    }

    const slides = /** @type {HTMLElement[]} */ ([...swiperEl.querySelectorAll('swiper-slide')]);
    swiperFallback.remove();

    Object.assign(swiperEl, this.#getSwiperConfig());

    swiperEl.initialize();
    swiperEl.removeAttribute('init');

    requestAnimationFrame(() => {
      if (mobileRoot) mobileRoot.style.minHeight = '';
    });

    const swiper = swiperEl.swiper;
    if (!swiper) return;

    const initialIndex = swiper.realIndex ?? 0;
    this.#mobileActiveIndex = initialIndex;

    this.#preloadAdjacent(slides, initialIndex, { currentOnly: true });

    const initialContent = this.#getMobileSlideContent(slides[initialIndex]);
    if (initialContent) this.#maybeAutoplayVideo(initialContent);

    const handleTouchStart = () => {
      this.#preloadAdjacent(slides, swiper.realIndex ?? 0);
    };

    const handleSlideChange = () => {
      const newIndex = swiper.realIndex ?? 0;
      this.#preloadAdjacent(slides, newIndex);

      if (newIndex !== this.#mobileActiveIndex) {
        const previousContent = this.#getMobileSlideContent(slides[this.#mobileActiveIndex]);
        if (previousContent) this.#pauseDeferredMedia(previousContent);
      }

      const currentContent = this.#getMobileSlideContent(slides[newIndex]);
      if (currentContent) this.#maybeAutoplayVideo(currentContent);

      this.#mobileActiveIndex = newIndex;
      this.#syncCompositeVisibility();
    };

    swiper.on('touchStart', handleTouchStart);
    swiper.on('slideChangeTransitionStart', handleSlideChange);

    this.#swiperCleanup = () => {
      swiper.off('touchStart', handleTouchStart);
      swiper.off('slideChangeTransitionStart', handleSlideChange);
    };

    swiper.update();
  }

  /**
   * Return the media container inside a swiper-slide wrapper.
   * @param {Element | undefined} slide
   * @returns {HTMLElement | null}
   */
  #getMobileSlideContent(slide) {
    return /** @type {HTMLElement | null} */ (slide?.querySelector('.ax-gallery__mobile-slide') ?? null);
  }

  /**
   * @returns {Record<string, any>}
   */
  #getSwiperConfig() {
    return {
      slidesPerView: 1,
      slidesPerGroup: 1,
      speed: 400,
      grabCursor: true,
      watchOverflow: true,
      a11y: true,
      keyboard: { enabled: true },
      spaceBetween: 0,
      pagination: {
        enabled: true,
        clickable: true,
        // Cap the visible dots (otherwise products with many media show a huge dot row),
        // but keep a few main bullets at full size so the row reads clearly.
        dynamicBullets: true,
        dynamicMainBullets: 3,
      },
    };
  }

  /**
   * Dispatch removeLazyKeep for slides near the given index.
   * @param {HTMLElement[]} slides
   * @param {number} index
   * @param {{ currentOnly?: boolean }} [options]
   */
  #preloadAdjacent(slides, index, { currentOnly = false } = {}) {
    if (!slides.length) return;

    const offsets = currentOnly ? [0] : [0, 1, -1];

    for (const offset of offsets) {
      const target = slides[index + offset];
      if (target) this.#removeLazyKeep(target);
    }
  }

  /**
   * Dispatch removeLazyKeep if the given subtree still has any [data-lazy-keep].
   * @param {HTMLElement} element
   */
  #removeLazyKeep(element) {
    if (!element.querySelector('[data-lazy-keep]')) return;

    document.dispatchEvent(
      new CustomEvent('removeLazyKeep', {
        detail: { lazyParentElement: element },
      })
    );
  }
}

if (!customElements.get('ax-product-media-gallery')) {
  customElements.define('ax-product-media-gallery', AxProductMediaGallery);
}
